import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import { Pencil, Trash2, Plus, X, GripVertical, Eye, EyeOff, ListTree, Coffee } from 'lucide-react';
import './AdminPastries.css'; // Reusing some styles

const AdminMenu = () => {
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subTab, setSubTab] = useState('products'); // 'products' or 'categories'

    // Category Modal
    const [isCatModalOpen, setIsCatModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [catFormData, setCatFormData] = useState({ nom: '' });

    // Product Modal
    const [isProdModalOpen, setIsProdModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [prodFormData, setProdFormData] = useState({
        nom: '',
        description: '',
        prix: '',
        disponible: true,
        stock: 0,
        afficherStock: true,
        lowStockThreshold: 5,
        categorie: '',
        photo: null
    });

    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [catRecords, prodRecords] = await Promise.all([
                pb.collection('categories_carte').getFullList({ sort: '+ordre' }),
                pb.collection('produits_carte').getFullList({ sort: '+ordre', expand: 'categorie' })
            ]);
            setCategories(catRecords);
            setProducts(prodRecords);
        } catch (err) {
            console.error("Error fetching menu data:", err);
        } finally {
            setLoading(false);
        }
    };

    // --- Category Logic ---
    const openCatModal = (cat = null) => {
        if (cat) {
            setEditingCategory(cat);
            setCatFormData({ nom: cat.nom });
        } else {
            setEditingCategory(null);
            setCatFormData({ nom: '' });
        }
        setIsCatModalOpen(true);
    };

    const handleCatSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCategory) {
                await pb.collection('categories_carte').update(editingCategory.id, catFormData);
            } else {
                const newOrder = categories.length > 0 ? Math.max(...categories.map(c => c.ordre || 0)) + 1 : 1;
                await pb.collection('categories_carte').create({ ...catFormData, ordre: newOrder });
            }
            setIsCatModalOpen(false);
            fetchData();
        } catch (err) {
            alert("Erreur lors de la sauvegarde de la catégorie.");
        }
    };

    const deleteCategory = async (id) => {
        if (window.confirm("Supprimer cette catégorie ? Cela ne supprimera pas les produits, mais ils n'auront plus de catégorie.")) {
            try {
                await pb.collection('categories_carte').delete(id);
                fetchData();
            } catch (err) {
                alert("Erreur lors de la suppression.");
            }
        }
    };

    // --- Product Logic ---
    const openProdModal = (prod = null) => {
        if (prod) {
            setEditingProduct(prod);
            setProdFormData({
                nom: prod.nom,
                description: prod.description,
                prix: prod.prix,
                disponible: prod.disponible,
                stock: prod.stock || 0,
                afficherStock: prod.hasOwnProperty('afficherStock') ? prod.afficherStock : true,
                lowStockThreshold: prod.lowStockThreshold || 5,
                categorie: prod.categorie,
                photo: null
            });
        } else {
            setEditingProduct(null);
            setProdFormData({
                nom: '',
                description: '',
                prix: '',
                disponible: true,
                stock: 0,
                afficherStock: true,
                lowStockThreshold: 5,
                categorie: categories[0]?.id || '',
                photo: null
            });
        }
        setIsProdModalOpen(true);
    };

    const handleProdInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (type === 'file') {
            setProdFormData({ ...prodFormData, [name]: files[0] });
        } else if (type === 'checkbox') {
            setProdFormData({ ...prodFormData, [name]: checked });
        } else {
            setProdFormData({ ...prodFormData, [name]: value });
        }
    };

    const handleProdSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = new FormData();
            Object.keys(prodFormData).forEach(key => {
                if (key === 'photo') {
                    if (prodFormData.photo) data.append('photo', prodFormData.photo);
                } else {
                    data.append(key, prodFormData[key]);
                }
            });

            if (editingProduct) {
                await pb.collection('produits_carte').update(editingProduct.id, data);
            } else {
                const newOrder = products.length > 0 ? Math.max(...products.map(p => p.ordre || 0)) + 1 : 1;
                data.append('ordre', newOrder);
                await pb.collection('produits_carte').create(data);
            }
            setIsProdModalOpen(false);
            fetchData();
        } catch (err) {
            alert("Erreur lors de la sauvegarde du produit.");
        }
    };

    const deleteProduct = async (id) => {
        if (window.confirm("Supprimer ce produit ?")) {
            try {
                await pb.collection('produits_carte').delete(id);
                fetchData();
            } catch (err) {
                alert("Erreur lors de la suppression.");
            }
        }
    };

    const toggleProdVisibility = async (prod) => {
        try {
            const newStatus = !prod.disponible;
            await pb.collection('produits_carte').update(prod.id, { disponible: newStatus });
            setProducts(prev => prev.map(p => p.id === prod.id ? { ...p, disponible: newStatus } : p));
        } catch (err) {
            alert("Erreur.");
        }
    };

    // --- Drag & Drop ---
    const onDragStart = (e, index, type) => {
        const list = type === 'cat' ? categories : products;
        setDraggedItem({ index, type, item: list[index] });
        e.target.style.opacity = '0.5';
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
        setDragOverItem(index);
    };

    const onDrop = async (e, targetIndex, type) => {
        e.preventDefault();
        if (draggedItem.type !== type) return;

        const list = type === 'cat' ? [...categories] : [...products];
        const sourceIndex = draggedItem.index;

        if (sourceIndex === targetIndex) return;

        const [removed] = list.splice(sourceIndex, 1);
        list.splice(targetIndex, 0, removed);

        if (type === 'cat') setCategories(list);
        else setProducts(list);

        try {
            const collection = type === 'cat' ? 'categories_carte' : 'produits_carte';
            const updates = list.map((item, idx) =>
                pb.collection(collection).update(item.id, { ordre: idx + 1 })
            );
            await Promise.all(updates);
            fetchData();
        } catch (err) {
            console.error("Reorder failed", err);
            fetchData();
        }
    };

    if (loading) return <div style={{ padding: '20px' }}>Chargement...</div>;

    return (
        <div className="admin-pastries-container">
            <div className="admin-menu-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                <button
                    className={`btn ${subTab === 'products' ? '' : 'btn-outline'}`}
                    onClick={() => setSubTab('products')}
                    style={{ background: subTab === 'products' ? 'var(--color-brown)' : 'transparent', color: subTab === 'products' ? 'white' : 'var(--color-brown)', border: '1px solid var(--color-brown)' }}
                >
                    <Coffee size={18} style={{ marginRight: '8px' }} /> Produits de la Carte
                </button>
                <button
                    className={`btn ${subTab === 'categories' ? '' : 'btn-outline'}`}
                    onClick={() => setSubTab('categories')}
                    style={{ background: subTab === 'categories' ? 'var(--color-brown)' : 'transparent', color: subTab === 'categories' ? 'white' : 'var(--color-brown)', border: '1px solid var(--color-brown)' }}
                >
                    <ListTree size={18} style={{ marginRight: '8px' }} /> Catégories
                </button>
            </div>

            {subTab === 'categories' ? (
                <div className="categories-section">
                    <div className="admin-tools">
                        <button className="btn" onClick={() => openCatModal()}>
                            <Plus size={16} /> Ajouter une catégorie
                        </button>
                    </div>
                    <table className="pastries-table">
                        <thead>
                            <tr>
                                <th style={{ width: '40px' }}></th>
                                <th>Nom de la catégorie</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map((cat, index) => (
                                <tr
                                    key={cat.id}
                                    draggable
                                    onDragStart={(e) => onDragStart(e, index, 'cat')}
                                    onDragOver={(e) => onDragOver(e, index)}
                                    onDrop={(e) => onDrop(e, index, 'cat')}
                                    onDragEnd={(e) => { e.target.style.opacity = '1'; setDragOverItem(null); }}
                                    className={dragOverItem === index ? 'drag-over' : ''}
                                >
                                    <td className="drag-handle-cell"><GripVertical size={18} /></td>
                                    <td>{cat.nom}</td>
                                    <td>
                                        <button className="action-btn btn-edit" onClick={() => openCatModal(cat)}><Pencil size={14} /></button>
                                        <button className="action-btn btn-delete" onClick={() => deleteCategory(cat.id)}><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="products-section">
                    <div className="admin-tools">
                        <button className="btn" onClick={() => openProdModal()}>
                            <Plus size={16} /> Ajouter un produit
                        </button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="pastries-table" style={{ minWidth: '800px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th>Image</th>
                                    <th>Nom</th>
                                    <th>Catégorie</th>
                                    <th>Prix</th>
                                    <th>Stock</th>
                                    <th>Visible</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((prod, index) => (
                                    <tr
                                        key={prod.id}
                                        draggable
                                        onDragStart={(e) => onDragStart(e, index, 'prod')}
                                        onDragOver={(e) => onDragOver(e, index)}
                                        onDrop={(e) => onDrop(e, index, 'prod')}
                                        onDragEnd={(e) => { e.target.style.opacity = '1'; setDragOverItem(null); }}
                                        className={dragOverItem === index ? 'drag-over' : ''}
                                    >
                                        <td className="drag-handle-cell"><GripVertical size={18} /></td>
                                        <td>
                                            {prod.photo ? (
                                                <img src={pb.files.getURL(prod, prod.photo, { thumb: '100x100' })} alt="" className="pastry-thumbnail" />
                                            ) : <div className="pastry-thumbnail" style={{ background: '#eee' }}></div>}
                                        </td>
                                        <td>{prod.nom}</td>
                                        <td><span className="badge-category" style={{ padding: '4px 8px', borderRadius: '4px', background: 'var(--color-gold-light)', fontSize: '0.8rem' }}>{prod.expand?.categorie?.nom || 'Sans catégorie'}</span></td>
                                        <td>{prod.prix} €</td>
                                        <td>
                                            <span style={{
                                                fontWeight: 'bold',
                                                color: prod.stock <= (prod.lowStockThreshold || 5) ? '#e74c3c' : '#27ae60'
                                            }}>
                                                {prod.stock || 0}
                                            </span>
                                            {prod.afficherStock && <span title="Affiché au client" style={{ marginLeft: '5px', opacity: 0.5 }}>👁️</span>}
                                        </td>
                                        <td>
                                            <button className="action-btn" onClick={() => toggleProdVisibility(prod)} style={{ color: prod.disponible ? '#27ae60' : '#bdc3c7' }}>
                                                {prod.disponible ? <Eye size={18} /> : <EyeOff size={18} />}
                                            </button>
                                        </td>
                                        <td>
                                            <button className="action-btn btn-edit" onClick={() => openProdModal(prod)}><Pencil size={14} /></button>
                                            <button className="action-btn btn-delete" onClick={() => deleteProduct(prod.id)}><Trash2 size={14} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modals are exactly like AdminPastries but with menu specific fields */}
            {isCatModalOpen && (
                <div className="modal-overlay" onClick={() => setIsCatModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingCategory ? 'Modifier' : 'Ajouter'} une catégorie</h2>
                            <button className="close-btn" onClick={() => setIsCatModalOpen(false)}><X /></button>
                        </div>
                        <form onSubmit={handleCatSubmit}>
                            <div className="form-group">
                                <label>Nom de la catégorie</label>
                                <input type="text" value={catFormData.nom} onChange={e => setCatFormData({ nom: e.target.value })} required placeholder="Ex: Nos Thés Bio" />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn" style={{ background: '#999' }} onClick={() => setIsCatModalOpen(false)}>Annuler</button>
                                <button type="submit" className="btn">Sauvegarder</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isProdModalOpen && (
                <div className="modal-overlay" onClick={() => setIsProdModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingProduct ? 'Modifier' : 'Ajouter'} un produit à la carte</h2>
                            <button className="close-btn" onClick={() => setIsProdModalOpen(false)}><X /></button>
                        </div>
                        <form onSubmit={handleProdSubmit}>
                            <div className="form-group">
                                <label>Nom</label>
                                <input name="nom" value={prodFormData.nom} onChange={handleProdInputChange} required />
                            </div>
                            <div className="form-group">
                                <label>Catégorie</label>
                                <select name="categorie" value={prodFormData.categorie} onChange={handleProdInputChange} required>
                                    <option value="">Sélectionner une catégorie...</option>
                                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.nom}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea name="description" value={prodFormData.description} onChange={handleProdInputChange} />
                            </div>
                            <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>Prix (€)</label>
                                    <input type="number" step="0.01" name="prix" value={prodFormData.prix} onChange={handleProdInputChange} />
                                </div>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>Stock</label>
                                    <input type="number" name="stock" value={prodFormData.stock} onChange={handleProdInputChange} />
                                </div>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>Seuil d'alerte</label>
                                    <input type="number" name="lowStockThreshold" value={prodFormData.lowStockThreshold} onChange={handleProdInputChange} />
                                </div>
                            </div>
                            <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input type="checkbox" name="disponible" checked={prodFormData.disponible} onChange={handleProdInputChange} style={{ width: 'auto', marginRight: '10px' }} />
                                    Disponible
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input type="checkbox" name="afficherStock" checked={prodFormData.afficherStock} onChange={handleProdInputChange} style={{ width: 'auto', marginRight: '10px' }} />
                                    Montrer stock au client
                                </label>
                            </div>
                            <div className="form-group">
                                <label>Photo</label>
                                <input type="file" name="photo" onChange={handleProdInputChange} accept="image/*" />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn" style={{ background: '#999' }} onClick={() => setIsProdModalOpen(false)}>Annuler</button>
                                <button type="submit" className="btn">Sauvegarder</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminMenu;
